import axios from 'axios';
import { getApiBaseUrl } from './api';
import type { SessionAnswerValue, SessionKind, SessionQuestion } from '../types/aai';

export type CompanionLens = 'owner' | 'champion' | 'head' | 'employee';
export type CompanionMood = 'clear' | 'waiting' | 'nudge' | 'snoozed';

export interface CompanionSessionCard {
  id: string;
  description: string;
  sender: string;
  kind: SessionKind;
  questions: (SessionQuestion & { answer?: SessionAnswerValue })[];
}

export interface CompanionTask {
  key: string;
  kind: 'questions' | 'session' | 'ideas' | 'team' | 'reply';
  title: string;
  detail: string;
  count: number;
  link: string;
  cta: string;
  /** On a session: what the companion needs to show it as cards. */
  session?: CompanionSessionCard;
  /** On a reply: the notice to mark read when it is opened. */
  notification_id?: string;
}

export interface CompanionSession {
  id: string;
  title: string;
  sender: string;
  sent_at: string | null;
  open: number;
  link: string;
}

export interface CompanionSummary {
  on: boolean;
  lens: CompanionLens | null;
  tasks: CompanionTask[];
  total: number;
  mood: CompanionMood;
  new_sessions: CompanionSession[];
  snoozed_until: string | null;
}

export interface CompanionChatOpportunity {
  id: string;
  title: string;
  summary: string;
  kind: 'skill' | 'plugin' | 'build';
  status: 'suggested' | 'added' | 'dismissed';
  /** Where it was filed once added: a problem card on Opportunities, or a request on the ladder. */
  filed?: { where: 'opportunities' | 'request'; id: string; link?: string; status?: string };
}

export interface CompanionChatMessage {
  role: 'user' | 'assistant';
  text: string;
  at: string;
  opportunity?: CompanionChatOpportunity;
}

export interface CompanionChat {
  messages: CompanionChatMessage[];
}

const base = () => `${getApiBaseUrl()}/aai/companion`;

export const companionApi = {
  get: () => axios.get<CompanionSummary>(base()),
  seen: (total: number) => axios.post<{ snoozed_until: string | null }>(`${base()}/seen`, { total }),
  snooze: (total: number) => axios.post<{ snoozed_until: string | null }>(`${base()}/snooze`, { total }),
  chat: () => axios.get<CompanionChat>(`${base()}/chat`),
  say: (text: string) => axios.post<CompanionChat>(`${base()}/chat`, { text }),
  decide: (opportunityId: string, action: 'add' | 'dismiss') =>
    axios.post<CompanionChat>(`${base()}/chat/opportunities/${encodeURIComponent(opportunityId)}`, { action }),
  restart: () => axios.delete<CompanionChat>(`${base()}/chat`),
  toWorkspace: (opportunityId?: string) =>
    axios.post<{ id: string; path: string }>(`${base()}/chat/workspace`, { opportunity_id: opportunityId || null }),
};
